import { catchError, firstValueFrom, switchMap } from "rxjs";

import { AccountService } from "@bitwarden/common/auth/abstractions/account.service";
import { getUserId } from "@bitwarden/common/auth/services/account.service";
import { LogService } from "@bitwarden/common/platform/abstractions/log.service";
import { asUuid, SdkService } from "@bitwarden/common/platform/abstractions/sdk/sdk.service";
import { OrganizationId } from "@bitwarden/common/types/guid";
import type { RotationClient, OrganizationId as SdkOrganizationId } from "@bitwarden/sdk-internal";

import type {
  AccessConnectorDetail,
  AccessConnectorId,
  AccessConnectorRegistrationResponse,
  AccessConnector,
  RotationConfigCreateRequest,
  RotationConfigDetailView,
  RotationConfigId,
  RotationConfigView,
  TargetSystemCreateRequest,
  TargetSystemId,
  TargetSystemView,
} from "./rotation";
import { QuartzSchedulePreset, TargetSystemStatus } from "./rotation";
import { RotationConfigDescription, RotationSdkService } from "./rotation-sdk.service";

export class DefaultRotationSdkService extends RotationSdkService {
  constructor(
    private sdkService: SdkService,
    private accountService: AccountService,
    private logService: LogService,
  ) {
    super();
  }

  /** `description` reaches the log, so it names the operation and never its arguments. */
  private async withRotationClient<T>(
    description: string,
    operation: (rotation: RotationClient) => Promise<T> | T,
  ): Promise<T> {
    const userId = await firstValueFrom(this.accountService.activeAccount$.pipe(getUserId));
    return firstValueFrom(
      this.sdkService.userClient$(userId).pipe(
        switchMap(async (sdk) => {
          using ref = sdk.take();
          return await operation(ref.value.commercial().pam().rotation());
        }),
        catchError((error: unknown) => {
          this.logService.error(`Failed to ${description}: ${error}`);
          throw error;
        }),
      ),
    );
  }

  async listConnectors(organizationId: OrganizationId): Promise<AccessConnector[]> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    return this.withRotationClient("list access connectors", (rotation) =>
      rotation.connectors().list(orgId),
    );
  }

  async getConnector(
    organizationId: OrganizationId,
    id: AccessConnectorId,
  ): Promise<AccessConnectorDetail> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    return this.withRotationClient("get access connector", (rotation) =>
      rotation.connectors().get(orgId, id),
    );
  }

  async registerConnector(
    organizationId: OrganizationId,
    name: string,
  ): Promise<AccessConnectorRegistrationResponse> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    // The result carries the one-time token, so nothing here may log it.
    return this.withRotationClient("register access connector", (rotation) =>
      rotation.connectors().register(orgId, name),
    );
  }

  async enableConnector(organizationId: OrganizationId, id: AccessConnectorId): Promise<void> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    await this.withRotationClient("enable access connector", (rotation) =>
      rotation.connectors().enable(orgId, id),
    );
  }

  async disableConnector(organizationId: OrganizationId, id: AccessConnectorId): Promise<void> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    await this.withRotationClient("disable access connector", (rotation) =>
      rotation.connectors().disable(orgId, id),
    );
  }

  async deleteConnector(organizationId: OrganizationId, id: AccessConnectorId): Promise<void> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    await this.withRotationClient("delete access connector", (rotation) =>
      rotation.connectors().delete(orgId, id),
    );
  }

  async assignTarget(
    organizationId: OrganizationId,
    id: AccessConnectorId,
    targetSystemId: TargetSystemId,
  ): Promise<void> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    await this.withRotationClient("assign a target system to an access connector", (rotation) =>
      rotation.connectors().assign_target(orgId, id, targetSystemId),
    );
  }

  async unassignTarget(
    organizationId: OrganizationId,
    id: AccessConnectorId,
    targetSystemId: TargetSystemId,
  ): Promise<void> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    await this.withRotationClient("unassign a target system from an access connector", (rotation) =>
      rotation.connectors().unassign_target(orgId, id, targetSystemId),
    );
  }

  async listTargetSystems(organizationId: OrganizationId): Promise<TargetSystemView[]> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    return this.withRotationClient("list target systems", (rotation) =>
      rotation.target_systems().list(orgId),
    );
  }

  async createTargetSystem(
    organizationId: OrganizationId,
    request: TargetSystemCreateRequest,
  ): Promise<TargetSystemView> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    return this.withRotationClient("create a target system", (rotation) =>
      rotation.target_systems().create(orgId, request),
    );
  }

  async updateTargetSystem(
    organizationId: OrganizationId,
    id: TargetSystemId,
    request: TargetSystemCreateRequest,
  ): Promise<void> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    await this.withRotationClient("update a target system", (rotation) =>
      rotation.target_systems().update(orgId, id, request),
    );
  }

  async enableTargetSystem(organizationId: OrganizationId, id: TargetSystemId): Promise<void> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    await this.withRotationClient("enable a target system", (rotation) =>
      rotation.target_systems().enable(orgId, id),
    );
  }

  async disableTargetSystem(organizationId: OrganizationId, id: TargetSystemId): Promise<void> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    await this.withRotationClient("disable a target system", (rotation) =>
      rotation.target_systems().disable(orgId, id),
    );
  }

  async deleteTargetSystem(organizationId: OrganizationId, id: TargetSystemId): Promise<void> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    await this.withRotationClient("delete a target system", (rotation) =>
      rotation.target_systems().delete(orgId, id),
    );
  }

  async listConfigs(organizationId: OrganizationId): Promise<RotationConfigView[]> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    return this.withRotationClient("list rotation configs", (rotation) =>
      rotation.configs().list(orgId),
    );
  }

  async getConfig(
    organizationId: OrganizationId,
    id: RotationConfigId,
  ): Promise<RotationConfigDetailView> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    return this.withRotationClient("get a rotation config", (rotation) =>
      rotation.configs().get(orgId, id),
    );
  }

  async createConfig(
    organizationId: OrganizationId,
    request: RotationConfigCreateRequest,
  ): Promise<RotationConfigDetailView> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    return this.withRotationClient("create a rotation config", (rotation) =>
      rotation.configs().create(orgId, request),
    );
  }

  async updateConfig(
    organizationId: OrganizationId,
    id: RotationConfigId,
    request: RotationConfigCreateRequest,
  ): Promise<RotationConfigDetailView> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    return this.withRotationClient("update a rotation config", (rotation) =>
      rotation.configs().update(orgId, id, request),
    );
  }

  async pauseConfig(organizationId: OrganizationId, id: RotationConfigId): Promise<void> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    await this.withRotationClient("pause a rotation config", (rotation) =>
      rotation.configs().pause(orgId, id),
    );
  }

  async resumeConfig(organizationId: OrganizationId, id: RotationConfigId): Promise<void> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    await this.withRotationClient("resume a rotation config", (rotation) =>
      rotation.configs().resume(orgId, id),
    );
  }

  async rotateNow(organizationId: OrganizationId, id: RotationConfigId): Promise<void> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    await this.withRotationClient("dispatch an on-demand rotation", (rotation) =>
      rotation.configs().rotate_now(orgId, id),
    );
  }

  async recordManualRotation(organizationId: OrganizationId, id: RotationConfigId): Promise<void> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    await this.withRotationClient("record a manual rotation", (rotation) =>
      rotation.configs().record_manual_rotation(orgId, id),
    );
  }

  async deleteConfig(organizationId: OrganizationId, id: RotationConfigId): Promise<void> {
    const orgId = asUuid<SdkOrganizationId>(organizationId);
    await this.withRotationClient("delete a rotation config", (rotation) =>
      rotation.configs().delete(orgId, id),
    );
  }

  async describeConfigs(
    configs: readonly RotationConfigView[],
    targetStatusById: ReadonlyMap<TargetSystemId, TargetSystemStatus>,
  ): Promise<Map<RotationConfigId, RotationConfigDescription>> {
    // One client take for the whole list; both predicates are synchronous over it.
    return this.withRotationClient("describe rotation configs", (rotation) => {
      const configsClient = rotation.configs();
      const schedule = rotation.schedule();
      return new Map(
        configs.map((config) => [
          config.id,
          {
            actions: configsClient.actions(
              config,
              targetStatusById.get(config.targetSystemId) ?? null,
            ),
            schedulePreset: schedule.preset_for_cron(config.scheduleCron),
          },
        ]),
      );
    });
  }

  async presetForCron(cron: string | null): Promise<QuartzSchedulePreset> {
    return this.withRotationClient("resolve a schedule preset", (rotation) =>
      rotation.schedule().preset_for_cron(cron),
    );
  }

  async cronForPreset(preset: QuartzSchedulePreset): Promise<string | null> {
    return this.withRotationClient("resolve a preset's cron expression", (rotation) => {
      const cron = rotation.schedule().cron_for_preset(preset);
      return cron ?? null;
    });
  }

  async isLikelyQuartzCron(value: string): Promise<boolean> {
    return this.withRotationClient("check a cron expression's shape", (rotation) =>
      rotation.schedule().is_likely_quartz_cron(value),
    );
  }
}
